// @flow
import * as R from "ramda";

import type {
  FieldId,
  RoomId,
  ChecklistFieldTypes,
  SectionId,
  Section as SectionType,
  Breakpoint,
  FieldsById,
} from "src/types";
import type { Layout as LayoutType } from "src/types";
import { getDefaultFieldLayout } from "src/components/Manage/Builder/Checklist/SettingsBuilder/Layout/utils";

import {
  breakpoints,
  breakpointMaxWidths,
} from "src/components/Dock/DockLayout/utils";

export const gridTemplates = {
  [breakpoints.small]: "1fr",
  [breakpoints.medium]: "repeat(2, 1fr)",
  [breakpoints.large]: "repeat(3, 1fr)",
};

export type FieldConfig = {
  id: FieldId,
  w: number,
  type: string,
  maxWidth: number,
  fields?: Array<FieldId>,
  layout?: Array<FieldConfig>,
  startOnNewRow?: boolean,
};

export type Layout = Array<FieldConfig>;

export type ChecklistLayoutField = {
  id: FieldId,
  type: ChecklistFieldTypes,
  sectionId: SectionId,
  layout: LayoutType,
  fields: Array<ChecklistLayoutField>,
};

export type ChecklistLayoutFields = ChecklistLayoutField[];
export type VisibleFieldsIds = Array<FieldId>;

/**
 * Check whether a checklist field is set to be hidden based on the conditions settings of the checklist
 * @param {FieldId} id Field Id
 * @param {RoomId} roomId Room Id
 * @param {FieldsById} fieldsById Field details
 * @param {Object} behaviorByField behavior details of each field
 * @param {FieldId[]} hiddenFields list of hidden fields in a room
 * @return {boolean}
 */
export const isFieldHidden = ({
  id,
  roomId,
  fieldsById,
  behaviorByField,
  hiddenFields,
}: {
  id: FieldId,
  roomId: RoomId,
  fieldsById: FieldsById,
  behaviorByField: Object,
  hiddenFields: Array<FieldId>,
}) => {
  const details = fieldsById.get(`${id}`);
  const behavior = behaviorByField?.[roomId]?.[`${id}`]?.behavior || {};

  const fieldHidden = details ? details.get("hidden") : false;

  return (
    behavior?.current === "hidden" ||
    fieldHidden ||
    !hiddenFields.includes(id) === false
  );
};

/**
 * Remove out all the hidden checklist fields
 * @param {ChecklistLayoutFields} fields Checklist Layout fields
 * @param {RoomId} roomId Room Id
 * @param {FieldsById} fieldsById Field details
 * @param {Object} behaviorByField behavior details of each field
 * @param {FieldId[]} hiddenFields list of hidden fields in a room
 * @return {ChecklistLayoutFields} Return visible fields
 */
export const removeHiddenFields = ({
  fields,
  roomId,
  fieldsById,
  behaviorByField,
  hiddenFields,
}: {
  fields: ChecklistLayoutFields,
  roomId: RoomId,
  fieldsById: FieldsById,
  behaviorByField: Object,
  hiddenFields: Array<FieldId>,
}) => {
  const result = [];

  fields.forEach((field) => {
    if (field) {
      const hidden = isFieldHidden({
        id: field.id,
        roomId,
        fieldsById,
        behaviorByField,
        hiddenFields,
      });

      if (hidden) return;

      if (!field.fields) {
        result.push(field);
      } else {
        result.push({
          ...field,
          fields: removeHiddenFields({
            fields: field.fields,
            roomId,
            fieldsById,
            behaviorByField,
            hiddenFields,
          }),
        });
      }
    }
  });

  return result;
};

/**
 * Get the details of the checklist fields
 * @param {Array} fields Array of checklist field ids and checklist sections
 * @param {FieldsById} fieldsById fieldDetailsById
 * @return {ChecklistLayoutFields}
 */
export const addFieldDetails = ({
  fields,
  fieldsById,
}: {
  fields: $ReadOnlyArray<FieldId | SectionType>,
  fieldsById: FieldsById,
}) =>
  // $FlowFixMe
  fields.map((field) => {
    if (typeof field === "number") {
      const fieldDetails = fieldsById.get(`${field}`);
      if (fieldDetails)
        // $FlowFixMe
        return fieldDetails.toJS();
    } else if (fieldsById.get(`${field.sectionId}`)) {
      const sectionDetails = fieldsById.get(`${field.sectionId}`);
      return {
        ...field,
        // $FlowFixMe
        ...sectionDetails.toJS(),
        fields: addFieldDetails({
          fields: field.fields,
          fieldsById,
        }),
      };
    }
  });

/**
 * Given the checklist layout fields, generate each field's layout at each breakpoint
 * @param {ChecklistLayoutFields} fields Checklist layout fields
 * @param {Breakpoint} breakpoint Checklist breakpoints
 * @return {ChecklistLayoutFields}
 */

export const generateLayout = (
  visibleFields: ChecklistLayoutFields,
  breakpoint: Breakpoint,
) => {
  const fieldsInfo = visibleFields.map((field) => {
    if (
      field.sectionId &&
      (field.type === "section" || field.type === "subSection")
    ) {
      return {
        id: field.id,
        w: 1,
        maxWidth: breakpointMaxWidths[breakpoint],
        type: field.type,
        fields: R.pluck("id", field.fields),
        layout: generateLayout(field.fields, breakpoint),
      };
    } else {
      const defaultFieldLayout = getDefaultFieldLayout(field.type);
      const defaultMaxWidth = isNaN(parseInt(defaultFieldLayout.maxWidth))
        ? breakpointMaxWidths[breakpoint]
        : parseInt(defaultFieldLayout.maxWidth);

      const maxWidth = field.layout
        ? field.layout.maxWidth === "max"
          ? breakpointMaxWidths[breakpoint]
          : parseInt(field.layout.maxWidth) >= breakpointMaxWidths[breakpoint]
            ? breakpointMaxWidths[breakpoint]
            : parseInt(field.layout.maxWidth)
        : defaultMaxWidth;

      return {
        id: field.id,
        w: 1,
        maxWidth,
        type: field.type,
        startOnNewRow:
          field.layout === null ? false : field.layout.startOnNewRow,
      };
    }
  });

  let count = 0;
  const layout: Layout = fieldsInfo.map((field) => {
    if (
      count < breakpointMaxWidths[breakpoint] &&
      field.type !== "section" &&
      field.type !== "subSection" &&
      !field.startOnNewRow
    ) {
      if (field.maxWidth - field.w > 0) {
        if (
          field.maxWidth - field.w + count <
          breakpointMaxWidths[breakpoint]
        ) {
          field.w = field.maxWidth;
        } else {
          field.w = breakpointMaxWidths[breakpoint] - count;
        }
      }

      count += field.w;
    } else if (
      field.type === "section" ||
      field.type === "subSection" ||
      field.startOnNewRow
    ) {
      count = 0;
      field.w = field.maxWidth;
    } else {
      field.w = field.maxWidth;
      count = field.w;
    }
    return field;
  });
  return layout;
};

/**
 * Given the visible checklist fields, extract top level and nested fieldIds.
 * @param {ChecklistLayoutFields} fields Checklist layout fields
 * @return {VisibleFieldsIds}
 */
export function extractVisibleFieldIds(
  fields: ChecklistLayoutFields,
): VisibleFieldsIds {
  const fieldIds = [];

  function collectFields(fields: ChecklistLayoutFields) {
    fields.forEach((field) => {
      fieldIds.push(field.id);
      if (field.fields) {
        collectFields(field.fields);
      }
    });
  }
  collectFields(fields);

  return R.uniq(fieldIds);
}
